/*
 * Agency mode (draft 13, FEATURE-MAP 14): the rules for The Marketers, the only company with the agency trade. Deals and
 * their stages, the 3-month agreement and the monthly retainer bills it makes, when a saved card may be charged for one,
 * client health, lifecycle, onboarding tasks and the sales call slides. The three service brands and HQ never use these.
 */
import { addDays, daysBetween } from "./schedule.js";

/** The deal stages for an agency (the pipeline's stages, renamed). Won and Lost are fixed. */
export const AGENCY_STAGES: readonly { name: string; fixed: "won" | "lost" | null }[] = [
  { name: "New lead", fixed: null },
  { name: "Discovery call", fixed: null },
  { name: "Proposal sent", fixed: null },
  { name: "Negotiating", fixed: null },
  { name: "Won", fixed: "won" },
  { name: "Lost", fixed: "lost" },
];

/**
 * The local package's own monthly lines a proposal can add (owner answer 116): Google profile management is a separate
 * line, part of the local package. Prices are typed on each proposal.
 */
export const LOCAL_PACKAGE_LINES = ["Google profile management"] as const;

/** The agreement on every agency proposal with a monthly line (owner, 2026-10-22). */
export const AGREEMENT_TERM_MONTHS = 3;
export const AGREEMENT_NOTICE_DAYS = 30;

/** The words on the proposal page. The client reads these, so no colons and no dashes. */
export function agreementWords(p: { monthlyCents: number; setupCents: number; brand: string }): string[] {
  const money = (c: number) => `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return [
    `The plan is ${money(p.monthlyCents)} a month for at least ${String(AGREEMENT_TERM_MONTHS)} months.`,
    p.setupCents > 0
      ? `You pay the first month and the ${money(p.setupCents)} setup today when you sign.`
      : "You pay the first month today when you sign.",
    "Monthly billing starts one month after you sign.",
    `After ${String(AGREEMENT_TERM_MONTHS)} months it goes month to month. To stop, tell us ${String(AGREEMENT_NOTICE_DAYS)} days ahead.`,
    `Ad spend is paid to Meta or Google directly. It is never part of what ${p.brand} bills you.`,
  ];
}

/** The words next to the auto pay box. Ticking it is the written OK to charge the saved card each month. */
export function autoPayWords(p: { monthlyCents: number; brand: string }): string {
  const money = `$${(p.monthlyCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `I agree that ${p.brand} may charge my saved card ${money} each month for this plan until it ends.`;
}

export interface AgreementLine {
  amountCents: number;
  bill: "once" | "monthly";
  /** In the agreement: not optional, or optional and picked. */
  inPlan: boolean;
}

/** The monthly retainer and the one time setup from the picked lines. A plan needs a monthly line. */
export function agreementSplit(lines: readonly AgreementLine[]): { monthlyCents: number; setupCents: number } {
  const picked = lines.filter((l) => l.inPlan);
  return {
    monthlyCents: picked.filter((l) => l.bill === "monthly").reduce((s, l) => s + l.amountCents, 0),
    setupCents: picked.filter((l) => l.bill === "once").reduce((s, l) => s + l.amountCents, 0),
  };
}

/** The same day n months later, or the month's last day when it is shorter (Jan 31 plus 1 month is Feb 28). */
export function addMonths(date: string, n: number): string {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7)) - 1 + n;
  const yy = y + Math.floor(m / 12);
  const mm = ((m % 12) + 12) % 12;
  const last = new Date(Date.UTC(yy, mm + 1, 0)).getUTCDate();
  const d = Math.min(Number(date.slice(8, 10)), last);
  return `${String(yy)}-${String(mm + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export interface AgreementDates {
  /** The day they signed: month 1 starts here and is paid at signing. */
  startOn: string;
  termMonths: number;
  noticeDays: number;
  /** The day the client told us they want to stop, if they did. */
  noticeOn: string | null;
}

/** The day the minimum term is over. */
export function termEndsOn(a: Pick<AgreementDates, "startOn" | "termMonths">): string {
  return addMonths(a.startOn, a.termMonths);
}

/** The last day of service after notice: the term's end or the notice plus 30 days, whichever is later. */
export function endsOnAfterNotice(a: AgreementDates): string | null {
  if (!a.noticeOn) return null;
  const notice = addDays(a.noticeOn, a.noticeDays);
  const term = termEndsOn(a);
  return notice > term ? notice : term;
}

/**
 * The monthly bills due by today: month 2 onward (month 1 is the signing invoice), one each time the start day comes
 * round, while the month starts before the agreement ends. index 1 is month 2.
 */
export function retainerMonthsDue(a: AgreementDates, today: string): { index: number; billOn: string }[] {
  const ends = endsOnAfterNotice(a);
  const out: { index: number; billOn: string }[] = [];
  for (let i = 1; i < 600; i += 1) {
    const billOn = addMonths(a.startOn, i);
    if (billOn > today) break;
    if (ends && billOn >= ends) break;
    out.push({ index: i, billOn });
  }
  return out;
}

/** What the rest of the minimum term would cost: months in the term not billed yet, times the monthly. */
export function leftIfStoppedEarly(a: AgreementDates & { monthlyCents: number }, today: string): number {
  const billed = 1 + retainerMonthsDue({ ...a, noticeOn: null }, today).filter((m) => m.index < a.termMonths).length;
  return Math.max(0, a.termMonths - billed) * a.monthlyCents;
}

export type AgreementState = "active" | "notice" | "ended";

/** Active, on notice (they asked to stop, the last day is ahead) or ended. */
export function agreementState(a: AgreementDates, today: string): AgreementState {
  const ends = endsOnAfterNotice(a);
  if (!ends) return "active";
  return today >= ends ? "ended" : "notice";
}

/**
 * May a saved card be charged for this invoice with no one pressing anything? Only under the signed auto pay OK of the
 * agreement that made it, only for its own monthly bill (one line, never merged or added to), never more than the
 * monthly amount, and only while the agreement has not ended. Every charge path asks this one rule (old Steward lesson:
 * an OK is scoped by what is charged, not just by which record carries it).
 */
export function retainerChargeOk(p: {
  agreement: { id: string; autoPayOk: boolean; monthlyCents: number; state: AgreementState };
  invoice: { agreementId: string | null; kind: string | null; totalCents: number; lineCount: number; merged: boolean };
  hasCard: boolean;
}): boolean {
  return (
    p.hasCard &&
    p.agreement.autoPayOk &&
    p.agreement.state !== "ended" &&
    p.invoice.agreementId === p.agreement.id &&
    p.invoice.kind === "retainer" &&
    p.invoice.lineCount === 1 &&
    !p.invoice.merged &&
    p.invoice.totalCents > 0 &&
    p.invoice.totalCents <= p.agreement.monthlyCents
  );
}

// Clients --------------------------------------------------------------------------------------------------------------

export const LIFECYCLES = ["lead", "prospect", "client", "past"] as const;
export type Lifecycle = (typeof LIFECYCLES)[number];

export const LIFECYCLE_WORDS: Record<Lifecycle, string> = {
  lead: "Lead",
  prospect: "In talks",
  client: "Client",
  past: "Past client",
};

/** Client with a live plan, in talks with an open deal, past client once every plan ended, else a lead. */
export function lifecycleOf(p: { livePlans: number; endedPlans: number; openDeals: number }): Lifecycle {
  if (p.livePlans > 0) return "client";
  if (p.openDeals > 0) return "prospect";
  if (p.endedPlans > 0) return "past";
  return "lead";
}

export const HEALTHS = ["good", "watch", "bad", "none"] as const;
export type Health = (typeof HEALTHS)[number];

export const HEALTH_WORDS: Record<Health, string> = {
  good: "On track",
  watch: "Watch",
  bad: "Needs help",
  none: "No numbers yet",
};

/**
 * The health pill on a client's results. Leads this month so far against the same days last month, and the map rank
 * average now against before (lower is better). No numbers at all is "none".
 */
export function clientHealth(p: {
  leadsNow: number | null;
  leadsBefore: number | null;
  rankNow: number | null;
  rankBefore: number | null;
}): Health {
  const hasLeads = p.leadsNow !== null && p.leadsBefore !== null;
  const hasRank = p.rankNow !== null && p.rankBefore !== null;
  if (!hasLeads && !hasRank) return "none";
  const rankDrop = hasRank ? p.rankNow! - p.rankBefore! : 0;
  const leadsDown = hasLeads && p.leadsNow! < p.leadsBefore!;
  const bigLeadsDrop = hasLeads && p.leadsBefore! >= 4 && p.leadsNow! < p.leadsBefore! * 0.7;
  if (bigLeadsDrop || rankDrop >= 3) return "bad";
  if (leadsDown || rankDrop >= 1) return "watch";
  return "good";
}

/** Ad spend over leads, or null with no leads. */
export function costPerLead(spendCents: number, leads: number): number | null {
  return leads > 0 ? Math.round(spendCents / leads) : null;
}

/** This month so far and the same days of last month, for "leads vs last month". */
export function monthWindows(today: string): { now: { from: string; to: string }; before: { from: string; to: string } } {
  const from = `${today.slice(0, 7)}-01`;
  const lastFrom = addMonths(from, -1);
  const day = daysBetween(from, today);
  const lastEnd = addDays(from, -1);
  const lastTo = addDays(lastFrom, day) > lastEnd ? lastEnd : addDays(lastFrom, day);
  return { now: { from, to: today }, before: { from: lastFrom, to: lastTo } };
}

// Tasks and onboarding -------------------------------------------------------------------------------------------------

export const TASK_KINDS = ["todo", "call", "email", "meeting"] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

/** The onboarding checklist made for every new plan, with the days after signing each is due. */
export const ONBOARDING_TASKS: readonly { title: string; days: number; kind: TaskKind }[] = [
  { title: "Kickoff call with the client", days: 2, kind: "meeting" },
  { title: "Get manager access to their Google profile", days: 3, kind: "todo" },
  { title: "Get partner access to their Meta ad account", days: 3, kind: "todo" },
  { title: "Get access to Google Ads or Local Services", days: 5, kind: "todo" },
  { title: "Get their logo, photos and brand colors", days: 5, kind: "todo" },
  { title: "Set up lead forms and call tracking", days: 10, kind: "todo" },
  { title: "Run the first map rank grid", days: 10, kind: "todo" },
  { title: "Set the first monthly report date", days: 14, kind: "todo" },
];

/** "Nov 3, 2:00 PM CT" or "Nov 3" with no time: every agency time shows its time zone (owner rule). */
export function dueWords(dueOn: string, dueTime: string | null, timezone: string): string {
  const [y, m, d] = dueOn.split("-").map(Number) as [number, number, number];
  const day = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  if (!dueTime) return day;
  const [hh, mm] = dueTime.split(":").map(Number) as [number, number];
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${day}, ${String(h12)}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"} ${zoneShort(timezone)}`;
}

/** America/Chicago to CT, and so on; anything else as its city. */
export function zoneShort(timezone: string): string {
  const known: Record<string, string> = {
    "America/New_York": "ET",
    "America/Chicago": "CT",
    "America/Denver": "MT",
    "America/Phoenix": "MST",
    "America/Los_Angeles": "PT",
    "Asia/Manila": "PHT",
    "Asia/Kolkata": "IST",
    "Asia/Karachi": "PKT",
    UTC: "UTC",
  };
  return known[timezone] ?? timezone.split("/").at(-1)!.replace(/_/g, " ");
}

// Sales call slides ----------------------------------------------------------------------------------------------------

/** What is known about a business before a call. Every line on a slide comes from here; nothing is made up. */
export interface CallFacts {
  business: string;
  contact: string | null;
  service: string | null;
  area: string | null;
  website: string | null;
  /** Read from their site: it loads, shows a phone number, has a quote or contact form. Null when not read. */
  site: { loads: boolean; phoneShown: boolean; hasForm: boolean } | null;
  /** From their Google profile. Null when not found. */
  profile: { rating: number | null; reviewCount: number } | null;
  /** The map rank average from a grid that was run (paid, asked first). */
  mapRank: { keyword: string; average: number; top3: number; points: number } | null;
  /** Nearby businesses from a paid search (asked first). */
  competitors: { name: string; rating: number | null; reviewCount: number }[];
  /** The agency's own plan lines for the proposal slide. */
  offer: { description: string; monthlyCents: number | null; setupCents: number | null }[];
  agency: string;
}

export interface Slide {
  title: string;
  lines: string[];
}

const NOT_CHECKED = "We did not check this yet. We can look at it together on the call.";

/** Gaps seen from the facts alone, in plain words. */
export function callGaps(f: CallFacts): string[] {
  const out: string[] = [];
  if (f.profile === null) out.push("We could not find a Google profile for the business.");
  else {
    if (f.profile.reviewCount < 50) out.push(`The Google profile has ${String(f.profile.reviewCount)} reviews.`);
    if (f.profile.rating !== null && f.profile.rating < 4.5) out.push(`The Google rating is ${String(f.profile.rating)}.`);
  }
  if (!f.website) out.push("We did not find a website.");
  else if (f.site && !f.site.loads) out.push("The website did not load when we checked.");
  else if (f.site) {
    if (!f.site.phoneShown) out.push("The website does not show a phone number we could find.");
    if (!f.site.hasForm) out.push("The website has no quote or contact form we could find.");
  }
  if (f.mapRank && f.mapRank.average > 10)
    out.push(`On the map for "${f.mapRank.keyword}" the business shows at place ${f.mapRank.average.toFixed(1)} on average.`);
  const better = f.competitors.filter((c) => c.reviewCount > (f.profile?.reviewCount ?? 0));
  if (better.length === 1) out.push("1 nearby business has more Google reviews.");
  else if (better.length) out.push(`${String(better.length)} nearby businesses have more Google reviews.`);
  return out;
}

/** 8 to 10 slides from the facts, in order. Lines are built only from the facts. */
export function callSlides(f: CallFacts): Slide[] {
  const money = (c: number) => `$${(c / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  const slides: Slide[] = [
    {
      title: f.business,
      lines: [`A marketing review by ${f.agency}`, ...(f.contact ? [`For ${f.contact}`] : [])],
    },
    {
      title: "About the business",
      lines: [
        ...(f.service ? [`Does ${f.service}`] : []),
        ...(f.area ? [`Serves ${f.area}`] : []),
        ...(f.website ? [`Website ${f.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}`] : []),
      ],
    },
    {
      title: "Google profile",
      lines: f.profile
        ? [
            f.profile.rating === null ? "No rating yet." : `Rating ${String(f.profile.rating)} stars.`,
            `${String(f.profile.reviewCount)} reviews.`,
          ]
        : ["We could not find a Google profile for the business."],
    },
    {
      title: "Map rank",
      lines: f.mapRank
        ? [
            `Keyword "${f.mapRank.keyword}".`,
            `Average place ${f.mapRank.average.toFixed(1)} across ${String(f.mapRank.points)} points.`,
            `In the top 3 at ${String(f.mapRank.top3)} of ${String(f.mapRank.points)} points.`,
          ]
        : [NOT_CHECKED],
    },
    {
      title: "Website",
      lines: !f.website
        ? ["We did not find a website."]
        : !f.site
          ? [NOT_CHECKED]
          : [
              f.site.loads ? "The site loads." : "The site did not load when we checked.",
              f.site.phoneShown ? "A phone number shows on the site." : "We could not find a phone number on the site.",
              f.site.hasForm ? "There is a quote or contact form." : "We could not find a quote or contact form.",
            ],
    },
    {
      title: "Nearby businesses",
      lines: f.competitors.length
        ? f.competitors
            .slice(0, 5)
            .map(
              (c) =>
                `${c.name}, ${c.rating === null ? "no rating" : `${String(c.rating)} stars`}, ${String(c.reviewCount)} reviews.`,
            )
        : [NOT_CHECKED],
    },
    { title: "What we see", lines: callGaps(f).length ? callGaps(f) : ["Nothing stood out from what we checked."] },
    {
      title: "What we would do",
      lines: f.offer.length
        ? f.offer.map((o) =>
            [
              o.description,
              o.monthlyCents ? `${money(o.monthlyCents)} a month` : null,
              o.setupCents ? `${money(o.setupCents)} setup` : null,
            ]
              .filter(Boolean)
              .join(", "),
          )
        : ["We will build the plan together on the call."],
    },
    {
      title: "How it works",
      lines: [
        `${String(AGREEMENT_TERM_MONTHS)} month start, then month to month.`,
        "Pay the first month and setup when you sign.",
        `Stop any time after that with ${String(AGREEMENT_NOTICE_DAYS)} days notice.`,
        "Ad spend goes to Meta or Google directly.",
      ],
    },
    { title: "Next step", lines: ["We send a proposal today.", "Sign and pay online in a few minutes."] },
  ];
  return slides.map((s) => (s.lines.length ? s : { ...s, lines: [NOT_CHECKED] }));
}

/**
 * The numbers on the slides that are not in the facts (an AI writer must never add one). Empty means every number came
 * from the facts.
 */
export function inventedNumbers(slides: readonly Slide[], f: CallFacts): string[] {
  const known = new Set(
    (JSON.stringify(f).match(/\d+(?:\.\d+)?/g) ?? []).flatMap((n) => [n, String(Number(n)), Number(n).toFixed(1)]),
  );
  for (const n of [AGREEMENT_TERM_MONTHS, AGREEMENT_NOTICE_DAYS]) known.add(String(n));
  // Counts of the facts (how many nearby businesses) are facts too.
  for (let i = 0; i <= f.competitors.length; i += 1) known.add(String(i));
  for (const o of f.offer) for (const c of [o.monthlyCents, o.setupCents]) if (c) for (const part of money2(c)) known.add(part);
  const out: string[] = [];
  for (const s of slides)
    for (const line of [s.title, ...s.lines])
      for (const n of line.replace(/,(?=\d{3})/g, "").match(/\d+(?:\.\d+)?/g) ?? []) if (!known.has(n)) out.push(n);
  return [...new Set(out)];
}

function money2(c: number): string[] {
  const d = c / 100;
  return [String(d), d.toFixed(2), String(Math.trunc(d))];
}
